import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, Users, Award, TrendingUp, X } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedFeature, setSelectedFeature] = useState(null);

  React.useEffect(() => {
    if (user) {
      if (user.role === 'writer') navigate('/writer');
      else if (user.role === 'proofreader') navigate('/proofreader');
      else if (user.role === 'translator') navigate('/translator');
      else if (user.role === 'admin') navigate('/admin');
    }
  }, [user, navigate]);

  const features = [
    {
      id: 'submit',
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Dien Boeke In',
      description: 'Maklik om jou Afrikaanse boeke in te dien vir publikasie',
      details: {
        heading: 'Dien Jou Boek In',
        content: [
          { subtitle: 'Wie kan boeke indien?', text: 'Enige Afrikaanse skrywer van 15 jaar of ouer kan registreer en boeke indien.' },
          { subtitle: 'Aanvaarde formate', text: 'Ons aanvaar PDF, DOCX, en EPUB formate. Maksimum lêergrootte is 10MB.' },
          { subtitle: 'Die proses', items: [
            'Registreer as \'n skrywer op ons platform',
            'Laai jou manuskrip op met \'n voorblad',
            'Ons span sal jou boek evalueer',
            'Goedgekeurde boeke gaan deur proeflees',
            'Na proeflees word die boek gepubliseer'
          ]},
          { subtitle: 'Tantieme', text: 'Skrywers ontvang 70% van alle verkope. Die Biblioteek behou 30% vir bedryfskoste.' },
          { subtitle: 'Tydraamwerk', text: 'Die evaluasie proses neem gewoonlik 5-10 werksdae. Proeflees voeg nog 7-14 dae by.' }
        ]
      }
    },
    {
      id: 'proofread',
      icon: <Users className="w-8 h-8" />,
      title: 'Professionele Proeflees/Vertaling',
      description: 'Ervare proeflees en vertalers kyk jou boek deur',
      details: {
        heading: 'Proeflees & Vertaling',
        content: [
          { subtitle: 'Proeflees Vereistes', items: [
            'Moet 18 jaar of ouer wees',
            'Moet die proeflees toets slaag (70% of hoër)',
            'Moet bankbesonderhede verskaf vir betaling',
            'Aansoek moet deur admin goedgekeur word'
          ]},
          { subtitle: 'Proeflees Betaling', items: [
            'R2.00 per fout wat jy vind',
            'Geen basisbetaling - verdienste gebaseer op foute gevind',
            'Meer deeglike proeflees = hoër verdienste'
          ]},
          { subtitle: 'Vertaler Vereistes', items: [
            'Moet 18 jaar of ouer wees',
            'Moet die vertalingstoets slaag (70% of hoër)',
            'Moet bankbesonderhede verskaf vir betaling',
            'Aansoek moet deur admin goedgekeur word'
          ]},
          { subtitle: 'Vertaler Betaling', items: [
            'Kort boeke (<100 bladsye): R150 basis',
            'Lang boeke (≥100 bladsye): R300 basis',
            'R2.00 word afgetrek vir elke fout gevind deur proefleser',
            '84+ foute = Boek verwerp (geen betaling)'
          ]},
          { subtitle: 'Belangrike Reëls', items: [
            'Vertalers en proeflees sien nooit mekaar se name nie',
            '4 verwerpings lei tot skorsing van jou rekening',
            'Alle betalings word maandeliks verwerk'
          ]}
        ]
      }
    },
    {
      id: 'earn',
      icon: <Award className="w-8 h-8" />,
      title: 'Verdien Geld',
      description: 'Proeflees kan R100 per boek verdien',
      details: {
        heading: 'Verdien Geld met Die Biblioteek',
        content: [
          { subtitle: 'Skrywers', items: [
            '70% van alle boekverkope',
            'Maandelikse uitbetalings',
            'Geen voorafkoste om te publiseer'
          ]},
          { subtitle: 'Proeflees', items: [
            'R2.00 per fout wat jy vind',
            'Geen basisbetaling - verdien gebaseer op foute',
            'Geen beperking op hoeveel boeke jy kan proeflees'
          ]},
          { subtitle: 'Vertalers', items: [
            'R150 - R300 per boek (afhangende van lengte)',
            'Minus R2.00 per fout gevind deur proefleser',
            'Vra boeke aan wat jy wil vertaal'
          ]},
          { subtitle: 'Betaling Proses', items: [
            'Betalings word maandeliks verwerk',
            'Direkte EFT na jou bankrekening',
            'Volledige transaksie geskiedenis beskikbaar'
          ]},
          { subtitle: 'Belasting', text: 'Jy is verantwoordelik vir jou eie belastingverpligtinge. Ons verskaf jaarlikse verdienste opsommings.' }
        ]
      }
    },
    {
      id: 'grow',
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Groei Jou Platform',
      description: 'Bereik meer lesers deur ons platform',
      details: {
        heading: 'Groei met Die Biblioteek',
        content: [
          { subtitle: 'Vir Skrywers', items: [
            'Toegang tot \'n gevestigde Afrikaanse lesersmark',
            'Professionele proeflees verseker kwaliteit',
            'Digitale publikasie in EPUB formaat',
            'Bemarking ondersteuning vir jou boeke'
          ]},
          { subtitle: 'Vir Proeflees & Vertalers', items: [
            'Bou jou portfolio met elke boek',
            'Buigsame werksure - werk wanneer jy wil',
            'Konstante vloei van nuwe projekte',
            'Professionele ervaring in die uitgewersbedryf'
          ]},
          { subtitle: 'Platform Voordele', items: [
            'Veilige en betroubare betalingstelsel',
            'Toegewyde ondersteuning span',
            'Gereelde platform opdaterings',
            'Gemeenskap van Afrikaanse boekliefhebbers'
          ]},
          { subtitle: 'Ons Visie', text: 'Om die voorste platform te wees vir Afrikaanse digitale publikasies, waar skrywers, vertalers, en proeflees saam werk om kwaliteit Afrikaanse literatuur te bevorder.' }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_publish-flow-5/artifacts/qy7wfz5d_Die%20Biblioteek%20SA-Logo-Final.png" 
            alt="Die Biblioteek Logo" 
            className="w-full h-full object-contain"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center space-y-8">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              DIE BIBLIOTEEK
            </h1>
            <p className="text-xl sm:text-2xl text-gray-700 max-w-3xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
              Skrywer & Proefleser Platform vir Afrikaanse Boeke
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Button 
                data-testid="register-btn"
                size="lg" 
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-full shadow-lg transform transition hover:scale-105"
                onClick={() => navigate('/register')}
              >
                Begin Nou
              </Button>
              <Button 
                data-testid="login-btn"
                size="lg" 
                variant="outline" 
                className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg rounded-full"
                onClick={() => navigate('/login')}
              >
                Meld Aan
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <h2 className="text-3xl font-bold text-center text-gray-400 mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          DIE BIBLIOTEEK
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature) => (
            <FeatureCard 
              key={feature.id}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              onClick={() => setSelectedFeature(feature)}
            />
          ))}
        </div>
      </div>

      {/* Feature Detail Dialog */}
      <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {selectedFeature?.icon && (
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white">
                  {selectedFeature.icon}
                </div>
              )}
              {selectedFeature?.details?.heading}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {selectedFeature?.details?.content.map((section, index) => (
              <div key={index} className="space-y-2">
                <h3 className="text-lg font-semibold text-orange-600">{section.subtitle}</h3>
                {section.text && (
                  <p className="text-gray-700">{section.text}</p>
                )}
                {section.items && (
                  <ul className="space-y-1 ml-4">
                    {section.items.map((item, i) => (
                      <li key={`${section.title}-item-${i}`} className="text-gray-700 flex items-start gap-2">
                        <span className="text-orange-500 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          <div className="pt-4 border-t">
            <Button 
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              onClick={() => {
                setSelectedFeature(null);
                navigate('/register');
              }}
            >
              Registreer Nou
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-orange-400 to-orange-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Klaar om te begin?
          </h2>
          <p className="text-lg text-orange-50 mb-8">
            Sluit aan by ons gemeenskap van Afrikaanse skrywers, proeflees en vertalers
          </p>
          <Button 
            data-testid="cta-register-btn"
            size="lg" 
            className="bg-white text-orange-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-full shadow-lg transform transition hover:scale-105"
            onClick={() => navigate('/register')}
          >
            Registreer Vandag
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                DIE BIBLIOTEEK
              </p>
              <p className="text-sm text-gray-400 mt-1">
                © 2025 Die Biblioteek. Alle regte voorbehou.
              </p>
            </div>
            
            <div className="flex items-center space-x-6">
              <Link 
                to="/privacy-policy" 
                className="text-sm text-gray-300 hover:text-orange-400 transition-colors"
              >
                Privaatheidsbeleid/Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link 
                to="/terms-and-conditions" 
                className="text-sm text-gray-300 hover:text-orange-400 transition-colors"
              >
                Terme en Voorwaardes/Terms and Conditions
              </Link>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-400">
                Kontak: <a href="mailto:Leesinafrikaans@diebiblioteek.co.za" className="hover:text-orange-400 transition-colors">Leesinafrikaans@diebiblioteek.co.za</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, onClick }) => {
  return (
    <div 
      className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition transform hover:-translate-y-1 cursor-pointer"
      onClick={onClick}
      data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center text-white mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        {title}
      </h3>
      <p className="text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>
        {description}
      </p>
      <p className="text-orange-500 text-sm mt-3 font-medium">Klik vir meer inligting →</p>
    </div>
  );
};

export default LandingPage;